import React, { useState } from "react";
import { ChevronDown, ChevronUp, ChevronRight, Trash2 } from "react-feather";
import { C } from "../reporting/reportingStyles";
import BilingualField from "./BilingualField";
import {
  RichTextBlockEditor,
  AccordionBlockEditor,
  TableBlockEditor,
  CalloutBlockEditor,
  ContactBlockEditor,
  DividerBlockEditor,
} from "./blocks/BlockEditors";
import { BLOCK_TYPES, BLOCK_TYPE_LABELS, LIMITS, newBlock } from "../../constants/merchantPages";
import { inputStyle, iconBtn, hintStyle, btnSecondary, btnDanger } from "./pageStyles";

const EDITORS = {
  richText: RichTextBlockEditor,
  accordion: AccordionBlockEditor,
  table: TableBlockEditor,
  callout: CalloutBlockEditor,
  contact: ContactBlockEditor,
  divider: DividerBlockEditor,
};

/** Types whose heading is optional and usually blank, so the field starts hidden behind a link. */
const HEADING_HIDDEN = ["callout", "divider"];

const card = {
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  background: C.white,
  marginBottom: 12,
  boxShadow: C.shadow,
};

const typeChip = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: C.coralDark,
  background: C.coralLight,
  borderRadius: 6,
  padding: "3px 8px",
  whiteSpace: "nowrap",
};

/**
 * The ordered list of blocks that makes a page. Controlled — `blocks` in, `onChange(blocks)` out —
 * in the same style as ReceiptLayoutEditor: reorder with arrows, no drag-and-drop library.
 */
const SectionsEditor = ({ blocks = [], onChange, disabled = false }) => {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showHeading, setShowHeading] = useState(() => new Set());

  const update = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };
  const remove = (i) => {
    onChange(blocks.filter((_, j) => j !== i));
    setConfirmRemove(null);
  };
  const add = (type) => {
    if (!type || blocks.length >= LIMITS.maxBlocks) return;
    const block = newBlock(type);
    onChange([...blocks, block]);
    setCollapsed((s) => { const n = new Set(s); n.delete(block.id); return n; });
  };
  const toggle = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allCollapsed = blocks.length > 0 && blocks.every((b) => collapsed.has(b.id));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <strong style={{ fontSize: 14, color: C.text, flex: 1 }}>Sections ({blocks.length})</strong>
        {blocks.length > 1 && (
          <button
            type="button"
            style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}
            onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(blocks.map((b) => b.id)))}
          >
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        )}
      </div>

      {blocks.length === 0 && (
        <div style={{ ...card, padding: 28, textAlign: "center", color: C.textMed, fontSize: 13 }}>
          No sections yet. Add one below — a page needs at least one before it can be published.
        </div>
      )}

      {blocks.map((block, i) => {
        const Editor = EDITORS[block.type] || DividerBlockEditor;
        const isCollapsed = collapsed.has(block.id);
        const title = block.heading?.en || block.heading?.ar || "";
        const headingOpen = !HEADING_HIDDEN.includes(block.type) || showHeading.has(block.id) || Boolean(title);

        return (
          <div key={block.id} style={card} data-testid="section-card">
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: isCollapsed ? "none" : `1px solid ${C.borderLight}` }}>
              <button type="button" style={{ ...iconBtn, border: "none" }} onClick={() => toggle(block.id)} title={isCollapsed ? "Expand" : "Collapse"} aria-expanded={!isCollapsed}>
                {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              </button>
              <span style={{ fontSize: 12, color: C.textLight, fontWeight: 700 }}>{i + 1}</span>
              <span style={typeChip}>{BLOCK_TYPE_LABELS[block.type] || block.type}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: title ? C.text : C.textLight, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {title || "Untitled section"}
              </span>
              {!disabled && (
                confirmRemove === block.id ? (
                  <>
                    <span style={{ fontSize: 12, color: C.textMed }}>Remove this section?</span>
                    <button type="button" style={{ ...btnDanger, padding: "5px 10px", fontSize: 12 }} onClick={() => remove(i)}>Remove</button>
                    <button type="button" style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }} onClick={() => setConfirmRemove(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button type="button" style={iconBtn} disabled={i === 0} onClick={() => move(i, -1)} title="Move up" aria-label={`Move section ${i + 1} up`}><ChevronUp size={14} /></button>
                    <button type="button" style={iconBtn} disabled={i === blocks.length - 1} onClick={() => move(i, 1)} title="Move down" aria-label={`Move section ${i + 1} down`}><ChevronDown size={14} /></button>
                    <button type="button" style={iconBtn} onClick={() => setConfirmRemove(block.id)} title="Remove" aria-label={`Remove section ${i + 1}`}><Trash2 size={14} /></button>
                  </>
                )
              )}
            </div>

            {!isCollapsed && (
              <div style={{ padding: 14 }}>
                {headingOpen ? (
                  <BilingualField
                    label="Heading"
                    hint="Headings appear in the table of contents. Leave empty to continue the previous section."
                    value={block.heading}
                    onChange={(heading) => update(i, { heading })}
                    maxLength={LIMITS.heading}
                    disabled={disabled}
                  />
                ) : (
                  !disabled && (
                    <button type="button" style={{ border: "none", background: "none", color: C.coral, fontSize: 12, fontWeight: 600, padding: 0, marginBottom: 10, cursor: "pointer" }}
                      onClick={() => setShowHeading((s) => new Set(s).add(block.id))}>
                      ＋ Add a heading
                    </button>
                  )
                )}
                <Editor block={block} onChange={(patch) => update(i, patch)} disabled={disabled} />
              </div>
            )}
          </div>
        );
      })}

      {!disabled && (
        <div style={{ marginTop: 8 }}>
          <select
            aria-label="Add section"
            style={{ ...inputStyle, width: "auto", minWidth: 240, fontWeight: 600, color: C.coral }}
            value=""
            disabled={blocks.length >= LIMITS.maxBlocks}
            onChange={(e) => add(e.target.value)}
          >
            <option value="">＋ Add section…</option>
            {BLOCK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label} — {t.hint}</option>
            ))}
          </select>
          {blocks.length >= LIMITS.maxBlocks && <div style={hintStyle}>A page can have at most {LIMITS.maxBlocks} sections.</div>}
        </div>
      )}
    </div>
  );
};

export default SectionsEditor;
